/**
 * Actions on the paired machine's models, shared by every page that offers
 * them (Library, Video, Audio): one way to download, never one per page.
 */
import { APIClient } from "../api/client.js";
import { store } from "../state/store.js";
import { routeToOwnMachine, waitForHostedResult } from "./hosted-jobs.js";

function targetWorker(): string | null {
	const target = store.get().executionTarget;
	return target.kind === "hosted" ? target.worker.worker_id : null;
}

/**
 * Put `modelId`'s files on the paired machine. `onText` hears what to show
 * while it runs. Resolves null on success, or the sentence to show on failure.
 */
/**
 * « Envoyer le rapport » : the paired machine sends the end of its journal.
 *
 * The click is the consent. What leaves is redacted on the machine (no token,
 * no folder name; agent/report.py) and kept with the job on the server, where
 * the people who can fix the failure read it. Returns null, or why not.
 */
export async function sendReport(aboutJobId?: string | null): Promise<string | null> {
	try {
		const job = await APIClient.submitHostedJob(
			aboutJobId ? { about_job_id: aboutJobId } : {},
			routeToOwnMachine(targetWorker()),
			"send_report"
		);
		await waitForHostedResult(job, () => {}, "Le rapport n'a pas pu partir.");
		return null;
	} catch (error) {
		return error instanceof Error ? error.message : "Le rapport n'a pas pu partir.";
	}
}

/**
 * Ask the paired machine to load a model the person just picked, at the size
 * they will ask for, so their Generate draws instead of loading (measured: a
 * first image went from 16 s plus the load to 4.6 s). Fire and forget: a
 * refusal (the model is not whole there yet) changes nothing the person sees.
 */
export async function prepareOnMachine(
	modelId: string,
	width: number,
	height: number
): Promise<void> {
	const worker = targetWorker();
	if (!worker) return;
	try {
		await APIClient.submitHostedJob(
			{ model_id: modelId, width, height },
			routeToOwnMachine(worker),
			"model_prepare"
		);
	} catch {
		// Nothing to say: the Generate will load it, as it always did.
	}
}

export async function downloadOnMachine(
	modelId: string,
	onText: (text: string) => void,
	/** The family asking, for a repository several share (app/core/family_models). */
	family?: string | null
): Promise<string | null> {
	onText("Envoi à votre ordinateur…");
	try {
		const job = await APIClient.submitHostedJob(
			family ? { model_id: modelId, family } : { model_id: modelId },
			routeToOwnMachine(targetWorker()),
			"model_download"
		);
		await waitForHostedResult(
			job,
			(progress) =>
				onText(
					progress.preparing
						? progress.message
						: `Téléchargement… ${Math.round(progress.percent)} %`
				),
			"Le téléchargement n'a pas abouti."
		);
		return null;
	} catch (error) {
		return error instanceof Error ? error.message : "Le téléchargement n'a pas abouti.";
	}
}
